// src/components/assistant/LibraryView.tsx
//
// Librería de documentos dentro del shell del asistente. Reusa el módulo de
// documentos existente (Drive multi-tenant, backend Django listo):
//   - Subir (DocumentUpload) con toggle Personal ↔ Empresa.
//   - Listar por visibilidad: Míos / De la empresa / Compartidos conmigo.
//   - Compartir con miembros de la org (ShareDialog).
//
// Solo usa endpoints existentes de `/api/v1/admin/documents` (ver
// modules/documents/api.ts); la org la decide el JWT en el backend — esta vista
// nunca manda org_id.
//
// Lenguaje visual: piezas del shell Orion (Card/PageHeader, tokens semánticos,
// esquinas 20 px, chips redondos, móvil de primera sin scroll horizontal).
//
// Nota: que el AGENTE responda fundamentado en estos documentos (RAG) es una
// pieza aparte (grounding en el backend) — esta vista es la UI de gestión.

import { useId, useState } from 'react';
import { Building2, Library, User, Users } from 'lucide-react';

import { DocumentUpload } from '@/modules/documents/components/DocumentUpload';
import { DocumentList } from '@/modules/documents/components/DocumentList';
import { ShareDialog } from '@/modules/documents/components/ShareDialog';
import type { DocumentOut, Tab } from '@/modules/documents/types';
import { Card, PageHeader } from '@/modules/orion-shell/ui';

const TABS: { key: Tab; label: string; hint: string; icon: React.ReactNode }[] = [
  { key: 'mine', label: 'Míos', hint: 'Documentos que tú subiste.', icon: <User size={15} aria-hidden /> },
  { key: 'org', label: 'De la empresa', hint: 'Visibles para toda tu organización.', icon: <Building2 size={15} aria-hidden /> },
  {
    key: 'shared_with_me',
    label: 'Compartidos conmigo',
    hint: 'Otros miembros de tu organización los compartieron contigo.',
    icon: <Users size={15} aria-hidden />,
  },
];

export function LibraryView() {
  const [tab, setTab] = useState<Tab>('mine');
  const [refreshKey, setRefreshKey] = useState(0);
  const [sharing, setSharing] = useState<DocumentOut | null>(null);
  const tabsId = useId();

  const refresh = () => setRefreshKey((k) => k + 1);
  const current = TABS.find((t) => t.key === tab);

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden bg-background">
      <div className="mx-auto w-full max-w-6xl space-y-5 px-4 py-6 sm:px-6">
        <PageHeader
          title={
            <span className="inline-flex items-center gap-2">
              <Library size={24} className="text-primary" aria-hidden /> Librería
            </span>
          }
          subtitle="Tus documentos, los de tu empresa y los que te compartieron."
        />

        {/* Escritorio: la lista manda y «Subir» va a un lado. Móvil: primero lo
            que ya tienes (la lista), luego subir — sin scroll horizontal. */}
        <div className="grid min-w-0 gap-5 lg:grid-cols-[minmax(0,1fr)_300px] lg:items-start">
          <Card title="Documentos" bodyClassName="space-y-3">
            <div role="tablist" aria-label="Visibilidad" className="flex flex-wrap gap-2">
              {TABS.map((t) => {
                const active = tab === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    role="tab"
                    id={`${tabsId}-${t.key}`}
                    aria-selected={active}
                    aria-controls={`${tabsId}-panel`}
                    onClick={() => setTab(t.key)}
                    className={`inline-flex min-h-[40px] items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none ${
                      active
                        ? 'border-primary/40 bg-primary/10 font-medium text-foreground'
                        : 'border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    {t.icon}
                    {t.label}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground">{current?.hint}</p>

            <div role="tabpanel" id={`${tabsId}-panel`} aria-labelledby={`${tabsId}-${tab}`} className="min-w-0">
              <DocumentList tab={tab} refreshKey={refreshKey} onShareClick={(doc) => setSharing(doc)} />
            </div>
          </Card>

          <Card title="Subir documento" className="lg:sticky lg:top-4">
            <DocumentUpload onUploaded={refresh} />
          </Card>
        </div>

        {sharing && <ShareDialog doc={sharing} onClose={() => setSharing(null)} onShared={refresh} />}
      </div>
    </div>
  );
}

export default LibraryView;
